import { useEffect, useRef } from 'react';

// 모달 스크롤 제어 훅 (iOS Safari/Chrome 대응 포함, 수평/수직 스크롤 지원) //
export default function useModalScroll(options = {}) {
  const {
    includeViewportSync = true,
    viewportTopCssVar = '--modal-viewport-top',
    viewportHeightCssVar = '--modal-viewport-height',
    viewportTopSource = 'scrollY',
    viewportLeftCssVar = '--modal-viewport-left',
    viewportWidthCssVar = '--modal-viewport-width',
    viewportLeftSource = 'scrollX'
  } = options;

  const topLevelRef = useRef();
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);

  useEffect(() => {
    const topLevelNode = topLevelRef.current;
    document.body.style.overflow = 'hidden';

    // iOS 뷰포트 바운스 방지: 모달 스크롤 경계 도달 시 touchmove 차단 //
    function handleTouchStart(e) {
      touchStartY.current = e.touches[0].clientY;
      touchStartX.current = e.touches[0].clientX;
    }

    function handleTouchMove(e) {
      const el = topLevelRef.current;
      if (!el) return;

      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const deltaY = currentY - touchStartY.current;
      const deltaX = currentX - touchStartX.current;

      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = el;
      const isScrollableV = scrollHeight > clientHeight;
      const isScrollableH = scrollWidth > clientWidth;

      // 수평/수직 모두 스크롤 불가능하면 모든 터치 차단 //
      if (!isScrollableV && !isScrollableH) {
        e.preventDefault();
        return;
      }

      // 주된 이동 방향 판단 //
      const isMovingVertically = Math.abs(deltaY) >= Math.abs(deltaX);

      // 수직 스크롤 바운스 차단 //
      if (isScrollableV && isMovingVertically) {
        const isAtTop = scrollTop <= 0;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

        if (isAtTop && deltaY > 0) { e.preventDefault(); return; }
        if (isAtBottom && deltaY < 0) { e.preventDefault(); return; }
      }

      // 수평 스크롤 바운스 차단 //
      if (isScrollableH && !isMovingVertically) {
        const isAtLeft = scrollLeft <= 0;
        const isAtRight = scrollLeft + clientWidth >= scrollWidth - 1;

        if (isAtLeft && deltaX > 0) { e.preventDefault(); return; }
        if (isAtRight && deltaX < 0) { e.preventDefault(); return; }
      }
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    // 모바일 브라우저 주소창 변동 시 viewport top/height CSS 변수 동기화 //
    function syncViewport() {
      if (!includeViewportSync) return;
      if (!topLevelRef.current) return;

      const vv = window.visualViewport;

      // ----- 세로(top/height) -----
      let viewportTop = 0;

      if (viewportTopSource === 'offsetTop') {
        viewportTop = vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
      }
      else if (viewportTopSource === 'innerMinusVV') {
        viewportTop = vv ? Math.max(0, Math.round(window.innerHeight - vv.height)) : 0;
      }
      else {
        viewportTop = Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0));
      }

      const viewportHeight = Math.max(0, Math.round(window.innerHeight));

      // ----- 가로(left/width) -----
      let viewportLeft = 0;

      if (viewportLeftSource === 'offsetLeft') {
        viewportLeft = vv ? Math.round(vv.offsetLeft || 0) : 0;
      }
      else {
        viewportLeft = Math.round(window.scrollX || window.pageXOffset || 0);
      }

      const viewportWidth = Math.max(
        0,
        Math.round((vv && vv.width) ? vv.width : window.innerWidth)
      );

      const el = topLevelRef.current;
      
      el.style.setProperty(viewportTopCssVar, `${viewportTop}px`);
      el.style.setProperty(viewportHeightCssVar, `${viewportHeight}px`);

      el.style.setProperty(viewportLeftCssVar, `${viewportLeft}px`);
      el.style.setProperty(viewportWidthCssVar, `${viewportWidth}px`);
    }

    syncViewport();

    if (includeViewportSync) {
      window.addEventListener('resize', syncViewport);
      window.addEventListener('orientationchange', syncViewport);

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', syncViewport);
        window.visualViewport.addEventListener('scroll', syncViewport);
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);

      if (includeViewportSync) {
        if (topLevelNode) {
          topLevelNode.style.removeProperty(viewportTopCssVar);
          topLevelNode.style.removeProperty(viewportHeightCssVar);
          topLevelNode.style.removeProperty(viewportLeftCssVar);
          topLevelNode.style.removeProperty(viewportWidthCssVar);
        }

        window.removeEventListener('resize', syncViewport);
        window.removeEventListener('orientationchange', syncViewport);

        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', syncViewport);
          window.visualViewport.removeEventListener('scroll', syncViewport);
        }
      }
    };
  }, [
    includeViewportSync,
    viewportHeightCssVar,
    viewportTopCssVar,
    viewportTopSource,
    viewportLeftCssVar,
    viewportWidthCssVar,
    viewportLeftSource
  ]);

  return topLevelRef;
}
